import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebaseApp';
import { toast } from 'react-toastify';
import { IWorkshopInfo, IWorkshopMetaInfo } from '../store/employmentModule/workshopModal';
import {
  collection,
  doc,
  getDocs,
  query,
  onSnapshot,
  where,
  QueryDocumentSnapshot,
  orderBy,
  startAfter,
  limit,
  getCountFromServer,
} from 'firebase/firestore';
import { userAccountStatus } from '../utils/userUtils';
import { getMultipleClientByIds } from './client.service';
import { getUserInfoById } from './user.service';
import { ISortModel } from '../utils/common';
import dayjs from 'dayjs';

export const addNewWorkshop = async (workshopInfo: IWorkshopInfo) => {
  try {
    if (!workshopInfo) {
      throw new Error();
    }
    const response: any = await httpsCallable(functions, 'createWorkshopV2')(workshopInfo);

    if (response.data.success) {
      toast.success('New Workshop Created Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Creating a Workshop!');
  }
};

export const editWorkshop = async (workshopInfo: IWorkshopInfo) => {
  try {
    if (!workshopInfo) {
      throw new Error();
    }
    const response: any = await httpsCallable(functions, 'editWorkshopV2')(workshopInfo);

    if (response.data.success) {
      toast.success('Workshop Updated Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Updating a Workshop!');
  }
};

export const deleteWorkshop = async (workshopId: string) => {
  try {
    const response: any = await httpsCallable(functions, 'deleteWorkshopV2')({ id: workshopId });
    if (response.data.success) {
      toast.success('Workshop Deleted Successfully!');
    }
  } catch (error: any) {
    toast.error('Error Deleting Workshop!');
  }
};

const paginationCursors: QueryDocumentSnapshot<any>[] = [];

let lastOrderField = '';
let lastOrderDirection: 'asc' | 'desc' = 'desc';

export const getWorkshopList = async (page: number, sortModel: ISortModel[], callback: Function) => {
  try {
    const pageSize = 10;
    const workshopsRef = collection(db, 'workshops');

    let orderField = 'createdAt';
    let orderDirection: 'asc' | 'desc' = 'desc';
    if (sortModel.length > 0) {
      orderField = sortModel[0].field || 'createdAt';
      orderDirection = sortModel[0].sort === 'asc' ? 'asc' : 'desc';
    }

    if (orderField !== lastOrderField || orderDirection !== lastOrderDirection || page === 1) {
      paginationCursors.length = 0;
      lastOrderField = orderField;
      lastOrderDirection = orderDirection;
    }

    const countSnap = await getCountFromServer(query(workshopsRef));
    const totalRecords = countSnap.data().count || 0;
    const totalPages = Math.ceil(totalRecords / pageSize);

    if (page > totalPages) {
      page = 1;
      paginationCursors.length = 0;
    }

    // Handle case insensitive sorting
    if (orderField === 'dateTime' || orderField === 'title' || orderField === 'location' || orderField === 'type') {
      const allSnap = await getDocs(query(workshopsRef));
      const allDocs = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (orderField === 'dateTime') {
        allDocs.sort((a: any, b: any) => {
          const dateA = dayjs(a.dateTime[0]?.timing).tz('America/New_York');
          const dateB = dayjs(b.dateTime[0]?.timing).tz('America/New_York');
          return orderDirection === 'asc' ? dateA.valueOf() - dateB.valueOf() : dateB.valueOf() - dateA.valueOf();
        });
      } else {
        allDocs.sort((a: any, b: any) => {
          const aVal = String(a[orderField] || '').toLowerCase();
          const bVal = String(b[orderField] || '').toLowerCase();
          return orderDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
      }

      const offset = (page - 1) * pageSize;
      const workshops = allDocs.slice(offset, offset + pageSize);

      callback({
        totalRecords,
        currentPage: page,
        workshops,
        totalPages,
      });
      return;
    }

    // Use pagination for default sorting
    const baseQuery = query(workshopsRef, orderBy(orderField, orderDirection));

    if (page > 1 && !paginationCursors[page - 2]) {
      let currentCursor: QueryDocumentSnapshot<any> | null =
        paginationCursors.length > 0 ? paginationCursors[paginationCursors.length - 1] : null;
      let cursorPageIndex = paginationCursors.length + 1;
      while (cursorPageIndex < page) {
        const stepQuery = currentCursor
          ? query(baseQuery, startAfter(currentCursor), limit(pageSize))
          : query(baseQuery, limit(pageSize));
        const stepSnap = await getDocs(stepQuery);
        if (stepSnap.empty) break;
        currentCursor = stepSnap.docs[stepSnap.docs.length - 1];
        paginationCursors[cursorPageIndex - 1] = currentCursor;
        cursorPageIndex++;
      }
    }

    const startCursor = page > 1 ? paginationCursors[page - 2] : undefined;
    let pageQuery = query(baseQuery, limit(pageSize));
    if (startCursor) {
      pageQuery = query(baseQuery, startAfter(startCursor), limit(pageSize));
    }
    const snapshot = await getDocs(pageQuery);

    if (snapshot.empty) {
      callback({
        totalRecords,
        currentPage: page,
        workshops: [],
        totalPages,
      });
      return;
    }

    paginationCursors[page - 1] = snapshot.docs[snapshot.docs.length - 1];

    const workshops: any = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const result = {
      totalRecords,
      currentPage: page,
      workshops,
      totalPages,
    };

    callback(result);
  } catch (err: any) {
    console.error('getWorkshopList error: ', err);
    toast.error('Error Retrieving Workshop Data!');
    callback({
      totalRecords: 0,
      currentPage: page,
      workshops: [],
      totalPages: 0,
    });
  }
};

export const getMultipleWorkshopsByIds = async (workshopIds: string[]) => {
  try {
    if (workshopIds.length) {
      const workshopRef = collection(db, 'workshops');
      const querySnapshot = await getDocs(query(workshopRef));
      const documents = querySnapshot.docs.filter((doc) => workshopIds.includes(doc.id));
      const docList: any = [];

      documents.forEach((doc) => {
        docList.push({ id: doc.id, ...doc.data() });
      });

      return docList;
    }
    return [];
  } catch (err: any) {
    toast.error('Error Retrieving Workshops!');
    return [];
  }
};

export const searchWorkshop = async (searchQuery: string, sortBy: string, sortOrder: string, page: number) => {
  try {
    if (!searchQuery) {
      throw new Error();
    }
    const pageSize = 10;
    const response: any = await httpsCallable(
      functions,
      'searchWorkShopByTitleV2'
    )({ query: searchQuery, page, pageSize, sortBy, sortOrder });

    const workshopData = response.data.data;
    const totalHits = response.data.total || 0;

    const workshopIds: any = [];
    let workshopList = [];

    if (workshopData.length > 0) {
      for (const data of workshopData) {
        workshopIds.push(data.id);
      }
      workshopList = await getMultipleWorkshopsByIds(workshopIds);
      workshopList.sort((a: any, b: any) => {
        switch (sortBy) {
          case 'createdAt': {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
          }
          // will add more cases here for other sort models
          default: {
            // by default sort by createdAt
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
          }
        }
      });
      const pageSize = 10;
      const totalPages = Math.ceil(totalHits / pageSize);
      const result: IWorkshopMetaInfo = {
        totalRecords: totalHits,
        currentPage: page,
        workshops: workshopList,
        totalPages: totalPages,
      };
      return result;
    } else {
      return {
        totalRecords: 0,
        currentPage: page,
        workshops: [],
        totalPages: 0,
      };
    }
  } catch (error) {
    toast.error('Error Searching Workshop!');
  }
};

export const getWorkshopById = async (id: string, callback: Function) => {
  try {
    if (id) {
      const colRef = doc(db, 'workshops', id);

      const unsubscribe = onSnapshot(colRef, (snapshot) => {
        if (snapshot.exists()) {
          const workshopData = { id: snapshot.id, ...snapshot.data() };
          callback(workshopData);
        } else {
          callback({});
        }
      });

      return unsubscribe;
    }

    callback({});
    return () => {};
  } catch (error: any) {
    toast.error('Error Subscribing to Workshop Data!');
    return () => {};
  }
};

export const fetchAllWorkshops = async (callback: Function) => {
  const workshops: any = [];
  try {
    const colRef = collection(db, 'workshops');
    const unsubscribe = onSnapshot(colRef, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const workshops: any[] = [];

        querySnapshot.forEach((doc) => {
          if (!doc.data().isAttendanceMarked) {
            workshops.push({ id: doc.id, ...doc.data() });
          }
        });

        callback(workshops);
      } else {
        callback([]);
      }
    });

    return unsubscribe;
  } catch (err: any) {
    console.log(err.message);
  }
  return workshops;
};

export const fetchRegisteredClientsForWorkshop = async (workshopId: string, callback: Function) => {
  try {
    const collectionRef = collection(db, 'clients');
    const q = query(collectionRef, where('workshopId', '==', workshopId));
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientIds: string[] = [];
        querySnapshot.docs.forEach((doc: any) => {
          if (doc.data().account !== userAccountStatus.DELETED && !doc.data().workshopAttendance) {
            clientIds.push(doc.id);
          }
        });
        const clientList = await getMultipleClientByIds(clientIds);
        callback(clientList);
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    throw new Error('Error While Fetching Client Data Registered in a Workshop');
  }
};

export const fetchClientIdsByWorkshopId = async (workshopId: string, callback: Function) => {
  try {
    const collectionRef = collection(db, 'clients');
    const q = query(collectionRef, where('workshopId', '==', workshopId));
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientIds: string[] = [];
        querySnapshot.docs.forEach((doc: any) => {
          if (doc.data().account !== userAccountStatus.DELETED && !doc.data().workshopAttendance) {
            clientIds.push(doc.id);
          }
        });
        callback(clientIds);
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    throw new Error('Error While Fetching Client Data Registered in a Workshop');
  }
};

export const markWorkshopAttendance = async (clientIds: string[], workshopId: string, markedBy: string) => {
  try {
    const payload = {
      ids: clientIds,
      workshopId: workshopId,
      markedBy: markedBy,
    };
    const response = await httpsCallable(functions, 'markClientsWorkshopAttendanceV2')(payload);
    return response.data;
  } catch (error) {
    toast.error('Error While Marking Workshop Attendance!');
  }
};

export const fetchClientsAttendedWorkshop = async (workshopId: string, callback: Function) => {
  try {
    const collectionRef = collection(db, 'clients');
    const q = query(collectionRef, where('workshopId', '==', workshopId));
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientIds: string[] = [];
        querySnapshot.docs.forEach((doc: any) => {
          if (doc.data().account !== userAccountStatus.DELETED && doc.data().workshopAttendance) {
            clientIds.push(doc.id);
          }
        });
        const clientList = await getMultipleClientByIds(clientIds);
        for (const client of clientList) {
          for (const item of client.workshopAttendance) {
            if (item.markedBy) {
              const userData: any = await getUserInfoById(item.markedBy);
              item.markedBy = userData?.firstName + ' ' + userData?.lastName;
            }
          }
        }
        callback(clientList);
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    throw new Error('Error While Fetching Client Data Attended a Workshop');
  }
};
